import {useState, useCallback, useEffect} from 'react';
import Button from '../../../components/button/button';
import Message from "../../../components/toast-message";
import Loader from "../../../components/form-loader/form-loader";
import PageContainer from "../../../components/page-container";
import {useAppInfo} from "../../../helpers/hooks/common-hook";
import {classNames} from "../../../helpers/utils/css-utils";
import {APP_NAVIGATION} from "../consts/navigations";
import SelectInput from "../../../components/forms/select";
import TextBox from "../../../components/forms/text-box";


const ttsModelsByProvider = {
    deepgram: [
        {
            name: "aura-2-thalia-en",
            value: "aura-2-thalia-en"
        },
    ],
    elevenlabs: [
        {
            name: "eleven_v3",
            value: "eleven_v3"
        },
        {
            name: "eleven_flash_v2_5",
            value: "eleven_flash_v2_5"
        },
        {
            name: "eleven_multilingual_v2",
            value: "eleven_multilingual_v2"
        }
    ],
    openai: [
        {
            name: "tts-1",
            value: "tts-1"
        },
        {
            name: "tts-1-hd",
            value: "tts-1-hd"
        }
    ]
};

const sttModelsByProvider = {
    deepgram: [
        {
            name: "nova-3",
            value: "nova-3"
        },
        {
            name: "nova-2",
            value: "nova-2"
        },
        {
             name: "nova",
             value: "nova"
        }
    ],
    assemblyai: [
        {
            name: "universal-streaming-english",
            value: "universal-streaming-english"
        },
        {
            name: "universal-streaming-multi",
            value: "universal-streaming-multi"
        }
    ],
    elevenlabs: [
        {
            name: "scribe_v1_realtime",
            value: "scribe_v1_realtime"
        },
        {
            name: "scribe_v1",
            value: "scribe_v1"
        }
    ],
};

function Badge({text, indicate}) {
    const svgClass = classNames(indicate? "fill-green-500": "fill-red-500", "h-1.5 w-1.5")
    return (
        <span
            className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            <svg className={svgClass} viewBox="0 0 6 6" aria-hidden="true">
                <circle cx={3} cy={3} r={3}/>
            </svg>
            {text}
        </span>
    )
}

export default function VoiceAISettings({}) {
    const {appId} = useAppInfo();
    
    // Text To Speech State
    const [ttsFormData, setTtsFormData] = useState({
        provider: 'deepgram',
        model: 'nova-3',
        apiKey: '*********************************'
    });
    const [ttsModels, setTtsModels] = useState([]);
    const [ttsError, setTtsError] = useState({ apiKey: false });
    const [ttsValidateStatus, setTtsValidateStatus] = useState({ status: true });

    // Speech To Text State
    const [sttFormData, setSttFormData] = useState({
        provider: 'deepgram',
        model: 'aura-2-thalia-en',
        apiKey: '*********************************'
    });
    const [sttModels, setSttModels] = useState([]);
    const [sttError, setSttError] = useState({ apiKey: false });
    const [sttValidateStatus, setSttValidateStatus] = useState({ status: true });

    const [formState, setFormState] = useState({
        processing: false,
        success: false,
        failed: false,
        message: ''
    });

    // Handle TTS Changes
    const handleTtsChange = useCallback(event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;
        const newFormData = {...ttsFormData};
        newFormData[targetName] = targetValue;
        setTtsError({ [targetName]: false });
        setTtsFormData(newFormData);
    });

    // Handle STT Changes
    const handleSttChange = useCallback(event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;
        const newFormData = {...sttFormData};
        newFormData[targetName] = targetValue;
        setSttError({ [targetName]: false });
        setSttFormData(newFormData);
    });

    const validateForm = () => {
        let valid = true;
        
        // Validate TTS
        if (ttsFormData.apiKey === '') {
            setTtsError(prev => ({...prev, apiKey: true}));
            valid = false;
        } else {
            setTtsError(prev => ({...prev, apiKey: false}));
        }

        // Validate STT
        if (sttFormData.apiKey === '') {
            setSttError(prev => ({...prev, apiKey: true}));
            valid = false;
        } else {
            setSttError(prev => ({...prev, apiKey: false}));
        }

        return valid;
    }

    const updateFormState = (processing, success, failed, message) => {
        setFormState(Object.assign({}, {processing, success, failed, message}))
    }

    const handleOnSubmit = async (event) => {
        event.preventDefault();
        if (!formState.processing && validateForm()) {
            updateFormState(true, false, false);
            // Simulate API call
            setTimeout(() => {
                 updateFormState(false, true, false, "Successfully saved");
            }, 1000);
        }
    };

    const handleValidateTtsApiKey = async () => {
        // Simulate API call
        setTtsValidateStatus({status: true});
    };

    const handleValidateSttApiKey = async () => {
        // Simulate API call
        setSttValidateStatus({status: true});
    };

    // Update TTS Models
    useEffect( () => {
        const providerModels = ttsModelsByProvider[ttsFormData.provider] || [];
        setTtsModels(providerModels);
        if (providerModels.length > 0 && (!ttsFormData.model || !providerModels.find(m => m.value === ttsFormData.model))) {
             setTtsFormData(prev => ({...prev, model: providerModels[0].value}));
        }
    }, [ttsFormData.provider]);

    // Update STT Models
    useEffect( () => {
        const providerModels = sttModelsByProvider[sttFormData.provider] || [];
        setSttModels(providerModels);
        if (providerModels.length > 0 && (!sttFormData.model || !providerModels.find(m => m.value === sttFormData.model))) {
             setSttFormData(prev => ({...prev, model: providerModels[0].value}));
        }
    }, [sttFormData.provider]);

    return (
        <PageContainer
            title="Voice AI Settings"
            subtitle="General setting for Voice AI" navigation={APP_NAVIGATION}>


            <form className="pt-4 flex flex-col gap-8" onSubmit={handleOnSubmit}>

                {formState.processing && <div className="p-4"><Loader/></div>}
                {formState.success && <Message text={formState.message} type="success"/>}
                {formState.failed && <Message text={formState.message} type="error"/>}

                {/* Speech To Text Section */}
                <div className="flex flex-col gap-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Speech To Text (STT)</h3>
                    
                    <SelectInput
                        rootClass="w-1/2"
                        label="Provider*"
                        id="stt_provider"
                        name="provider"
                        value={sttFormData.provider}
                        onChange={handleSttChange}
                        optionsComp={<>
                            <option value="deepgram">Deepgram</option>
                            <option value="assemblyai">AssemblyAI</option>
                            <option value="elevenlabs">Elevenlabs</option>
                        </>}
                    />

                    <SelectInput
                        rootClass="w-1/2"
                        label="Model*"
                        id="stt_model"
                        name="model"
                        value={sttFormData.model}
                        onChange={handleSttChange}
                        optionsComp={<>
                            {sttModels.map(model => (
                                <option key={model.value} value={model.value}>{model.name}</option>
                            ))}
                        </>}
                    />

                    <TextBox
                        rootClass="w-1/2"
                        label="API Key"
                        type="password"
                        name="apiKey"
                        id="stt_apiKey"
                        value={sttFormData.apiKey}
                        placeholder="Enter API Key"
                        onChange={handleSttChange}
                        error={sttError.apiKey}
                    />

                    <TextBox
                        rootClass="w-1/2"
                        label="End Of Turn Timeout (ms)"
                        type="number"
                        name="eotTimeout"
                        id="eotTimeout"
                        value={sttFormData.eotTimeout}
                        placeholder="200"
                        min="100"
                        max="5000"
                        onChange={handleSttChange}
                        error={sttError.eotTimeout}
                    />

                    <div className="bg-transparent flex flex-row items-center gap-4">
                        <Button color="default" variant="button" type="button" onClick={handleValidateSttApiKey} text="Validate Key" />
                        <Badge text={sttValidateStatus.status? "Valid Key": "Unverified"} indicate={sttValidateStatus.status}/>
                    </div>
                </div>

                {/* Text To Speech Section */}
                <div className="flex flex-col gap-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Text To Speech (TTS)</h3>
                    
                    <SelectInput
                        rootClass="w-1/2"
                        label="Provider*"
                        id="tts_provider"
                        name="provider"
                        value={ttsFormData.provider}
                        onChange={handleTtsChange}
                        optionsComp={<>
                            <option value="deepgram">Deepgram</option>
                            <option value="openai">OpenAI</option>
                            <option value="elevenlabs">ElevenLabs</option>
                        </>}
                    />

                    <SelectInput
                        rootClass="w-1/2"
                        label="Model*"
                        id="tts_model"
                        name="model"
                        value={ttsFormData.model}
                        onChange={handleTtsChange}
                        optionsComp={<>
                            {ttsModels.map(model => (
                                <option key={model.value} value={model.value}>{model.name}</option>
                            ))}
                        </>}
                    />

                    <TextBox
                        rootClass="w-1/2"
                        label="API Key"
                        type="password"
                        name="apiKey"
                        id="tts_apiKey"
                        value={ttsFormData.apiKey}
                        placeholder="Enter API Key"
                        onChange={handleTtsChange}
                        error={ttsError.apiKey}
                    />

                    <div className="bg-transparent flex flex-row items-center gap-4">
                        <Button color="default" variant="button" type="button" onClick={handleValidateTtsApiKey} text="Validate Key" />
                        <Badge text={ttsValidateStatus.status? "Valid Key": "Unverified"} indicate={ttsValidateStatus.status}/>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                    <Button variant="button" type="submit" text="Save All Settings" />
                </div>
            </form>

        </PageContainer>
    )
}
